import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import type { LedgerFlag, LedgerRow, SessionTrace } from '~/domain/layered';
import type { PocketColour, PocketId } from '~/domain/types';
import { formatCents } from '~/domain/profiles';
import { getWheel } from '~/domain/wheels';
import { layerColour } from '~/view/layers';
import { SESSION_END_LABELS } from '~/view/labels';

const FLAG_INFO: Readonly<Record<LedgerFlag, { label: string; title: string }>> = {
  capped: {
    label: 'capped',
    title: "A table maximum cut this spin's stake — the pile placed was smaller than the strategy asked for.",
  },
  skimmed: {
    label: 'skimmed',
    title: 'Winnings above the pocket threshold were locked away this spin and cannot be lost back.',
  },
  imprisoned: {
    label: 'imprisoned',
    title: 'A losing even-money stake was held over by en prison rather than lost outright.',
  },
};

/**
 * Shown by default, of a session that can run to hundreds of rows. The
 * question a ledger answers is "what happened at the end" — the run stops
 * where the gate fires — so the tail is the part worth reading and the
 * default window looks there rather than at the buy-in. Rendering every row
 * unconditionally would also put hundreds of DOM rows in front of a reader
 * who has not asked to scroll that far.
 */
const DEFAULT_WINDOW = 60;

/**
 * Colour for a genuinely signed figure — a net — never for a magnitude like a
 * stake or a rack balance, which is always non-negative and would read as
 * "this cost is good news" if it picked up gain/loss colour.
 */
function signClass(cents: number): string {
  if (cents > 0) return 'text-gain';
  if (cents < 0) return 'text-loss';
  return 'text-ink';
}

// Colour is a pure function of the pocket id (red/black by number, green for
// a zero), identical on both wheel styles for every id they share — '00' is
// the only pocket unique to one wheel, and it is green like '0' on the other.
// So either wheel's pocket map can answer it, which matters here because the
// props contract carries a trace and layer names, not a table, so there is no
// wheel style available to pick one deliberately.
const SINGLE_ZERO = getWheel('single-zero');
const DOUBLE_ZERO = getWheel('double-zero');

function pocketColour(id: PocketId): PocketColour {
  return (SINGLE_ZERO.pockets.get(id) ?? DOUBLE_ZERO.pockets.get(id))?.colour ?? 'black';
}

const POCKET_SWATCH: Readonly<Record<PocketColour, { background: string; border: string }>> = {
  red: { background: 'var(--color-pocket-red)', border: 'var(--color-pocket-red)' },
  // Black is close enough to the panel's own surfaces to disappear without a
  // visible edge, so it borrows a translucent white ring the way the wheel's
  // own read-only felt does for the same reason.
  black: { background: 'var(--color-pocket-black)', border: 'rgba(255,255,255,0.35)' },
  green: { background: 'var(--color-pocket-green)', border: 'var(--color-pocket-green)' },
};

/**
 * The session ledger (SPEC-V2.md §6.2): every spin of one seeded session, with
 * each spread's own contribution beside the total.
 *
 * This is the drill-down, never the headline. The trajectory chart directly
 * above it carries the caption naming it as one sample from the distribution,
 * and covers this table too — so the seed rides on the outcome line here
 * instead of a second paragraph repeating the point a centimetre lower.
 */
export interface LedgerTableProps {
  trace: SessionTrace;
  /** Layer names in stack order — the index is the colour key. */
  layerNames: readonly string[];
  currency: string;
  class?: string;
}

const LedgerTable: Component<LedgerTableProps> = (props) => {
  const [showAll, setShowAll] = createSignal(false);

  const totalRows = createMemo(() => props.trace.rows.length);
  const visibleRows = createMemo(() =>
    showAll() || totalRows() <= DEFAULT_WINDOW
      ? props.trace.rows
      : props.trace.rows.slice(-DEFAULT_WINDOW),
  );
  const hiddenCount = createMemo(() => totalRows() - visibleRows().length);

  const result = createMemo(() => props.trace.result);

  return (
    <section class={`rounded-lg border border-line bg-surface-1 p-3 ${props.class ?? ''}`}>
      <h2 class="mb-1 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
        Session ledger
      </h2>

      {/* The chart directly above carries the sample caption for this whole
          block, so the seed rides on the outcome line here rather than in a
          second paragraph saying the same thing a centimetre lower. */}
      <p class="tnum mt-1 text-[11px] text-ink-dim">
        Seed <span class="text-ink-faint">{props.trace.seed}</span> — ended{' '}
        <span class="text-ink">{SESSION_END_LABELS[result().end]}</span> after{' '}
        <span class="text-ink">{result().spins}</span> spins, net{' '}
        <span class={signClass(result().netCents)}>
          {formatCents(result().netCents, props.currency)}
        </span>
        .
      </p>

      {/* §9.3. The per-spread columns are the most over-readable thing here:
          a layer that carried this session did so on these spins, and the
          wheel has no memory of having favoured it. */}
      <p class="mt-1 text-[10px] text-ink-faint">
        The per-spread columns describe these spins, not what a spread tends to do.
      </p>

      <Show
        when={totalRows() > 0}
        fallback={
          <p class="mt-2 text-[11px] text-ink-faint">
            No spins were played this session — it ended before the wheel turned once. See the
            result above.
          </p>
        }
      >
        <div class="mt-2 max-h-[420px] overflow-auto rounded border border-line">
          <table class="w-full min-w-max border-collapse text-[11px]">
            <thead class="sticky top-0 z-10 bg-surface-2">
              <tr class="border-b border-line text-left text-[10px] uppercase tracking-wide text-ink-faint">
                <th class="py-1 pr-2 text-right font-medium">Spin</th>
                <th class="py-1 pr-2 font-medium">Pocket</th>
                <For each={props.layerNames}>
                  {(name, index) => (
                    <th class="py-1 pr-2 text-right font-medium">
                      <span class="inline-flex items-center justify-end gap-1">
                        <span
                          class="h-2 w-2 shrink-0 rounded-full"
                          style={{
                            background: layerColour(index()).hue,
                            'border-color': layerColour(index()).edge,
                            'border-style': 'solid',
                            'border-width': '1px',
                          }}
                        />
                        <span class="max-w-[64px] truncate normal-case tracking-normal text-ink-dim">
                          {name}
                        </span>
                      </span>
                    </th>
                  )}
                </For>
                <th class="py-1 pr-2 text-right font-medium">Total net</th>
                <th class="py-1 pr-2 text-right font-medium">At risk</th>
                <th class="py-1 pr-2 text-right font-medium">In rack</th>
                <th class="py-1 pr-2 text-right font-medium">Pocketed</th>
                <th class="py-1 pr-2 text-left font-medium">Flags</th>
              </tr>
            </thead>
            <tbody>
              <For each={visibleRows()}>
                {(row) => (
                  <LedgerRowView row={row} layerNames={props.layerNames} currency={props.currency} />
                )}
              </For>
            </tbody>
          </table>
        </div>

        <div class="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[10px] text-ink-faint">
          <Show when={hiddenCount() > 0} fallback={<span>All {totalRows()} spins shown.</span>}>
            <span>
              Showing the last {visibleRows().length} of {totalRows()} spins — the run ends where
              the gate fired, which is the part worth reading.
            </span>
          </Show>
          <Show when={totalRows() > DEFAULT_WINDOW}>
            <button
              type="button"
              class="rounded border border-line px-1.5 py-0.5 text-ink-dim transition-colors hover:border-line-strong hover:text-ink"
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll() ? `Show last ${DEFAULT_WINDOW}` : `Show all ${totalRows()}`}
            </button>
          </Show>
        </div>
      </Show>
    </section>
  );
};

const LedgerRowView: Component<{
  row: LedgerRow;
  layerNames: readonly string[];
  currency: string;
}> = (props) => {
  const money = (cents: number) => formatCents(cents, props.currency);
  const swatch = createMemo(() => POCKET_SWATCH[pocketColour(props.row.pocketId)]);

  return (
    <tr class="border-b border-line/60 last:border-0">
      <td class="tnum py-1 pr-2 text-right text-ink-dim">{props.row.spin}</td>
      <td class="py-1 pr-2">
        <span class="inline-flex items-center gap-1.5">
          <span
            class="h-2 w-2 shrink-0 rounded-full"
            style={{
              background: swatch().background,
              'border-color': swatch().border,
              'border-style': 'solid',
              'border-width': '1px',
            }}
          />
          <span class="tnum text-ink">{props.row.pocketId}</span>
        </span>
      </td>
      <For each={props.layerNames}>
        {(_, index) => {
          const cell = () => props.row.perLayer[index()];
          const net = () => cell()?.netCents ?? 0;
          return (
            <td class={`tnum py-1 pr-2 text-right ${signClass(net())}`}>{money(net())}</td>
          );
        }}
      </For>
      <td class={`tnum py-1 pr-2 text-right font-medium ${signClass(props.row.netCents)}`}>
        {money(props.row.netCents)}
      </td>
      <td class="tnum py-1 pr-2 text-right text-ink-dim">{money(props.row.atRiskCents)}</td>
      <td class="tnum py-1 pr-2 text-right text-ink-dim">{money(props.row.inRackCents)}</td>
      <td class="tnum py-1 pr-2 text-right text-ink-dim">{money(props.row.pocketedCents)}</td>
      <td class="py-1 pr-2">
        <Show when={props.row.flags.length > 0} fallback={<span class="text-ink-faint">—</span>}>
          <div class="flex flex-wrap gap-1">
            <For each={props.row.flags}>
              {(flag) => (
                <span
                  class="inline-flex items-center rounded-full border border-line bg-surface-2 px-1.5 py-0.5 text-[10px] text-ink-dim"
                  title={FLAG_INFO[flag].title}
                >
                  {FLAG_INFO[flag].label}
                </span>
              )}
            </For>
          </div>
        </Show>
      </td>
    </tr>
  );
};

export default LedgerTable;
